import React, { useEffect, useState } from 'react';
import { inventoryAPI } from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Ruler } from 'lucide-react';
import { toast } from 'sonner';

const STEEL_DENSITY = 7.86e-6; // kg per mm^3 (matches backend STEEL_DENSITY_CONST 7.86/1e6)

export function DuplicateSheetDialog({ item, onClose, onDone }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!item) { setForm(null); return; }
    setForm({
      unique_id: '',
      item_name: item.item_name || '',
      external_id: item.external_id || '',
      sheet_grade: item.sheet_grade || '',
      sheet_thickness: item.sheet_thickness != null ? String(item.sheet_thickness) : '',
      sheet_length: item.sheet_length != null ? String(item.sheet_length) : '',
      sheet_width: item.sheet_width != null ? String(item.sheet_width) : '',
    });
  }, [item]);

  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const weight = (() => {
    if (!form) return 0;
    const t = parseFloat(form.sheet_thickness), l = parseFloat(form.sheet_length), w = parseFloat(form.sheet_width);
    if (isNaN(t) || isNaN(l) || isNaN(w)) return 0;
    return l * w * t * STEEL_DENSITY;
  })();

  const save = async () => {
    if (!form.unique_id.trim()) { toast.error('New Unique ID is required'); return; }
    setSaving(true);
    try {
      const payload = {
        unique_id: form.unique_id.trim(),
        item_name: form.item_name,
        external_id: form.external_id,
        sheet_grade: form.sheet_grade,
        sheet_thickness: form.sheet_thickness === '' ? null : parseFloat(form.sheet_thickness),
        sheet_length: form.sheet_length === '' ? null : parseFloat(form.sheet_length),
        sheet_width: form.sheet_width === '' ? null : parseFloat(form.sheet_width),
      };
      const res = await inventoryAPI.duplicateSheetItem(item.id, payload);
      toast.success(`Created ${res.data.unique_id}${res.data.status === 'pending_approval' ? ' (pending admin approval)' : ''}`);
      onDone && onDone(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to duplicate item');
    } finally {
      setSaving(false);
    }
  };

  if (!form) return null;

  return (
    <Dialog open={!!item} onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="duplicate-sheet-dialog">
        <DialogHeader>
          <DialogTitle className="text-sm flex items-center gap-1.5">
            <Ruler className="w-4 h-4" /> Duplicate to New Sheet Size
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-[11px] text-muted-foreground">
            Cloning <span className="font-mono font-medium text-foreground">{item.unique_id}</span> — {item.item_name}.
            Set a new Unique ID and the new dimensions. All other master data is copied.
          </p>
          <div>
            <Label className="text-[10px]">New Unique ID *</Label>
            <Input value={form.unique_id} onChange={e => upd('unique_id', e.target.value)} placeholder="e.g. HRPO-3mmX1250mmX1500mm"
              className="h-8 text-xs font-mono" data-testid="dup-unique-id" />
          </div>
          <div>
            <Label className="text-[10px]">Item Name</Label>
            <Input value={form.item_name} onChange={e => upd('item_name', e.target.value)} className="h-8 text-xs" data-testid="dup-item-name" />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label className="text-[10px]">Grade</Label>
              <Input value={form.sheet_grade} onChange={e => upd('sheet_grade', e.target.value)} className="h-8 text-xs" data-testid="dup-grade" />
            </div>
            <div>
              <Label className="text-[10px]">Thickness (mm)</Label>
              <Input type="number" step="0.01" value={form.sheet_thickness} onChange={e => upd('sheet_thickness', e.target.value)} className="h-8 text-xs" data-testid="dup-thickness" />
            </div>
            <div>
              <Label className="text-[10px]">External ID</Label>
              <Input value={form.external_id} onChange={e => upd('external_id', e.target.value)} className="h-8 text-xs" data-testid="dup-external-id" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[10px]">Length (mm)</Label>
              <Input type="number" step="0.01" value={form.sheet_length} onChange={e => upd('sheet_length', e.target.value)} className="h-8 text-xs" data-testid="dup-length" />
            </div>
            <div>
              <Label className="text-[10px]">Width (mm)</Label>
              <Input type="number" step="0.01" value={form.sheet_width} onChange={e => upd('sheet_width', e.target.value)} className="h-8 text-xs" data-testid="dup-width" />
            </div>
          </div>
          {weight > 0 && (
            <div className="text-[11px] text-muted-foreground" data-testid="dup-weight-preview">
              Calculated sheet weight: <span className="font-semibold text-foreground">{weight.toFixed(3)} Kg</span>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose} data-testid="dup-cancel-btn">Cancel</Button>
          <Button size="sm" onClick={save} disabled={saving} data-testid="dup-save-btn">
            {saving && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />} Create Duplicate
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
